import React from "react";
import { Link, useLocation } from "react-router-dom";
import { NavDropdown } from "react-bootstrap";
import { useAuth } from "../providers/AuthProvider";
import { isSuperAdminPath, SUPERADMIN_ROOT } from "../../features/superadmin/navItems";
import { NotificationBell } from "./NotificationBell";
import { RoleSwitcher } from "./RoleSwitcher";

import logo from "../../htdocs/images/logo.png";
import logoMini from "../../htdocs/images/logo-mini.png";
import profilePhoto from "../../htdocs/images/faces/face28.png";

/**
 * Destino del logo. Keep the slug when the user is inside a tenant; el staff de
 * plataforma (TASK-22) no pertenece a ninguna academia, así que su "inicio" es
 * el panel y no la landing pública; legacy users go to "/".
 */
function resolveHome(user) {
  if (user?.slug) return `/${user.slug}/app/dashboard`;
  if (user?.is_staff) return "/superadmin";
  return "/";
}

function Navbar({ onToggleSidebar, sidebarOpen = false }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  const displayName = user?.first_name || user?.name || user?.email || "—";
  const homeTo = resolveHome(user);
  const hasTenant = Boolean(user?.slug);
  const profileTo = hasTenant ? `/${user.slug}/app/perfil` : "/profile";
  // Atajo al panel de plataforma para el staff (TASK-22). Hace falta porque el
  // login redirige a `/{slug}/app/dashboard` tomando el slug de la URL de
  // entrada: un miembro del staff que entra por la pantalla de una academia
  // aterriza en un dashboard donde no tiene rol de tenant y se queda sin forma
  // de llegar al panel. Se oculta cuando ya está dentro.
  const showAdminLink = user?.is_staff === true && !isSuperAdminPath(pathname);

  return (
    <nav className="app-navbar">
      <div className="app-navbar__brand">
        <Link to={homeTo}>
          <img src={logo} alt="Diezlar" className="brand-logo" />
          <img src={logoMini} alt="Diezlar" className="brand-logo-mini" />
        </Link>
      </div>

      <div className="app-navbar__menu">
        {/* Left: desktop minimize / mobile hamburger */}
        <button
          className="app-navbar__toggler"
          type="button"
          onClick={onToggleSidebar}
          aria-label="Mostrar u ocultar el menú lateral"
          aria-expanded={sidebarOpen}
        >
          <span className="mdi mdi-menu" aria-hidden="true" />
        </button>

        {/* Right: role switcher (dual-role) + notifications + profile */}
        <div className="app-navbar__right d-flex align-items-center gap-1">
          <RoleSwitcher />
          {/* Notificaciones y perfil son de tenant: `/notifications/` y
              `/companies/me/` responden 403 sin membresía. El staff de
              plataforma no pertenece a ninguna academia (no tiene slug), así
              que se ocultan en vez de dejar una campana que sondea un 403 cada
              minuto y un enlace a una página en blanco. Mismo criterio que
              RoleSwitcher, que ya se auto-oculta sin slug. */}
          {hasTenant && <NotificationBell />}
          <NavDropdown
            align="end"
            title={
              <span className="d-inline-flex align-items-center gap-2">
                <img
                  src={profilePhoto}
                  alt="profile"
                  width={32}
                  height={32}
                  style={{ borderRadius: "50%", objectFit: "cover" }}
                />
                <span className="d-none d-sm-inline">{displayName}</span>
              </span>
            }
            id="profile-dropdown"
          >
            {showAdminLink && (
              <>
                <NavDropdown.Item as={Link} to={SUPERADMIN_ROOT}>
                  <i className="mdi mdi-shield-account-outline text-primary me-2" />
                  Panel de plataforma
                </NavDropdown.Item>
                <NavDropdown.Divider />
              </>
            )}
            {hasTenant && (
              <>
                <NavDropdown.Item as={Link} to={profileTo}>
                  <i className="mdi mdi-face text-primary me-2" />
                  Perfil
                </NavDropdown.Item>
                <NavDropdown.Divider />
              </>
            )}
            <NavDropdown.Item onClick={logout}>
              <i className="mdi mdi-logout text-primary me-2" />
              Cerrar sesión
            </NavDropdown.Item>
          </NavDropdown>
        </div>
      </div>
    </nav>
  );
}

export { Navbar };
